// One query holds everything the screens read.
//
// The legacy application made five calls to draw its start page — /theme,
// /user, /entry, /messages and /representations — each of them travelling
// browser to connector to central API. This is one.

import { createContext, useContext, useEffect, type ReactNode } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api, ApiError, type Bootstrap, type Card } from "~/api";
import { setPrimary } from "~/theme";

const Ctx = createContext<Bootstrap | null>(null);

export function useBootstrap(): Bootstrap {
  const b = useContext(Ctx);
  if (!b) throw new Error("useBootstrap outside the provider");
  return b;
}

export function BootstrapProvider({
  children,
  fallback,
  signedOut,
}: {
  children: ReactNode;
  fallback: ReactNode;
  signedOut: ReactNode;
}) {
  const { data, isLoading, error } = useQuery({
    queryKey: ["bootstrap"],
    queryFn: () => api<Bootstrap>("/api/portal/bootstrap"),
    staleTime: 60_000,
    retry: (count, e) => !(e instanceof ApiError && e.isUnauthenticated) && count < 2,
  });

  // The customer's colour becomes the theme's primary — after the render, since
  // the theme is state other components read.
  const accent = data?.company.branding.accentColor;
  useEffect(() => {
    if (data) setPrimary(accent);
  }, [data, accent]);

  if (isLoading) return <>{fallback}</>;
  if (error instanceof ApiError && error.isUnauthenticated) return <>{signedOut}</>;
  if (!data) return <>{signedOut}</>;

  return <Ctx.Provider value={data}>{children}</Ctx.Provider>;
}

/**
 * useAnnouncements polls, at the rate the server asked for.
 *
 * The legacy application asked every three seconds. The job scheduler ticks
 * every ten, so nothing it was watching could change faster than that; two
 * requests in three were wasted. The interval comes from the payload so it can
 * be changed without redeploying this.
 */
export function useAnnouncements() {
  const b = useBootstrap();
  return useQuery({
    queryKey: ["announcements"],
    queryFn: () => api<Card[]>("/api/portal/announcements"),
    initialData: b.announcements,
    refetchInterval: (q) => {
      const cards = q.state.data ?? [];
      const busy = cards.some(
        (c) => c.state === "activating" || c.state === "deactivating" || c.errorStage > 0,
      );
      return busy ? b.poll.fastIntervalMs : b.poll.intervalMs;
    },
    refetchOnWindowFocus: true,
  });
}

/** useRefresh re-reads everything after a change. */
export function useRefresh() {
  const qc = useQueryClient();
  return () => {
    void qc.invalidateQueries({ queryKey: ["bootstrap"] });
    void qc.invalidateQueries({ queryKey: ["announcements"] });
  };
}
